<template>
  <v-card>
    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-title class="pa-0 pe-3">
          <v-card-title>
            {{ t('email_notification') }}
          </v-card-title>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row class="mt-0">
            <v-col cols="auto" class="border-e-md pt-0 d-flex flex-column">
              <v-row>
                <v-col>
                  <v-switch :label="t('enable_feature')" color="primary" v-model="enableFeature" hide-details/>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col class="align-self-end">
                  <v-btn variant="outlined" @click="submit" :disabled="validateEmails(emails) !== true">{{ t('save_settings') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="3">
              <v-textarea
                v-model="emails"
                :disabled="!enableFeature"
                :label="t('emails')"
                density="compact"
                :rules="[validateEmails]"
                :hint="t('emails_hint')"
                persistent-hint
              />
            </v-col>
            <v-col class="pt-0 d-flex flex-column">
              <v-textarea
                v-model="customMessage"
                :label="t('html_mail_body')"
                hide-details
                :disabled="!enableFeature"
                auto-grow
                clearable
              />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script setup>
import { ref, toRefs, toRaw, watch, onMounted } from "vue";
import { modifyPatchPark } from "../../remote/park.js";
import * as yup from 'yup';
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark']);
const { originalPark } = toRefs(props);
const emit = defineEmits(['updated']);

// Park Data
const enableFeature = ref(false);
const customMessage = ref("");
const emails = ref("");

function validateEmails(txt) {
  const emailSchema = yup.string().email();
  const emails = txt.split(',');

  try {
    // Validate each email in the list
    emails.forEach(email => {
      // Trim each email to remove any leading or trailing spaces
      const trimmedEmail = email.trim();
      emailSchema.validateSync(trimmedEmail);
    });
    return true;
  } catch (err) {
    return t('invalid_email_format');
  }
}

function setFromPark(park) {
  enableFeature.value = park['email_notif'] || false;
  emails.value = park['notif_emails'] || "";
  customMessage.value = park['notif_body_html'] || "";
}

async function submit() {
  let payload = {};
  if (enableFeature.value) {
    payload['email_notif'] = true;
    payload['notif_body_html'] = customMessage.value;
    payload['notif_emails'] = emails.value.replace(/\s*,\s*/g, ',');
  } else {
    payload['email_notif'] = false;
  }
  console.log(payload);
  loadingOverlay.value = true;
  try {
    await modifyPatchPark(useAlerts, payload, originalPark.value.id);
    emit('updated');
  } catch (e) {
    useAlerts.value.errorAlert(t('error_during_park_save'), e.toString());
  } finally {
    loadingOverlay.value = false;
  }
}

watch(originalPark, setFromPark);

onMounted(() => {
  if (originalPark.value !== null) {
    setFromPark(originalPark.value);
  }
});


</script>

<i18n>
{
  "en": {
    "email_notification": "Email Notification",
    "enable_feature": "Enable Feature",
    "save_settings": "Save settings",
    "emails": "Emails",
    "emails_hint": "Insert emails separated by commas. Example: test{'@'}ros.it,test2{'@'}ros.it",
    "html_mail_body": "HTML Mail Body",
    "invalid_email_format": "Invalid Format. Example: \"test1{'@'}ros.com,test2{'@'}ros.com,test3{'@'}ros.com\"",
    "error_during_park_save": "Error during Park save"
  },
  "it": {
    "email_notification": "Notifica Email",
    "enable_feature": "Abilita Funzionalità",
    "save_settings": "Salva impostazioni",
    "emails": "Email",
    "emails_hint": "Inserisci le email separate da virgole. Esempio: test{'@'}ros.it,test2{'@'}ros.it",
    "html_mail_body": "Corpo HTML della Mail",
    "invalid_email_format": "Formato non valido. Esempio: \"test1{'@'}ros.com,test2{'@'}ros.com,test3{'@'}ros.com\"",
    "error_during_park_save": "Errore durante il salvataggio del Parco"
  },
  "fr": {
    "email_notification": "Notification par Email",
    "enable_feature": "Activer la Fonctionnalité",
    "save_settings": "Enregistrer les paramètres",
    "emails": "Emails",
    "emails_hint": "Insérez les emails séparés par des virgules. Exemple: test{'@'}ros.it,test2{'@'}ros.it",
    "html_mail_body": "Corps HTML du Mail",
    "invalid_email_format": "Format invalide. Exemple: \"test1{'@'}ros.com,test2{'@'}ros.com,test3{'@'}ros.com\"",
    "error_during_park_save": "Erreur lors de l'enregistrement du Parc"
  }
}
</i18n>

<style scoped>

</style>
